import React, { ReactNode, forwardRef } from 'react';

import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withPrefix } from 'gatsby';
import { styled } from 'styled-components';

const CtaCardContainer = styled.a<{
  $bgColor?: string;
  $mobileBgColor?: string;
  $hoverBgColor?: string;
  $cardHeight?: string;
  $fullHeight?: boolean;
  $fadeIn?: boolean;
}>`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${(props) => props.$bgColor ?? props.theme.colors.whiteSmoke};
  text-decoration: none;
  color: inherit;
  position: relative;
  cursor: pointer;
  transition: background-color 0.3s;
  ${(props) => props.$fullHeight && 'height: 100%'};

  @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
    background-color: ${(props) => props.$mobileBgColor ?? props.theme.colors.whiteSmoke};
  }

  &:hover {
    background-color: ${(props) => props.$hoverBgColor ?? props.theme.colors.lighterGray};

    img {
      transform: scale(1.1);
    }
  }

  ${({ $fadeIn }) =>
    $fadeIn &&
    `
    animation: fade-in 1s;

    @keyframes fade-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 100;
      }
    }
  `}
`;

const FeaturedImageContainer = styled.div<{ $isCornerImage?: boolean; $bottomRightAlignment?: boolean }>`
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
    aspect-ratio: 2 / 1;
    object-fit: cover;
    transition: transform 0.5s ease-in-out;

    ${({ $isCornerImage }) =>
      $isCornerImage &&
      `
        width: auto;
        max-width: 6rem;
        margin-top: 2rem;
        margin-bottom: 0.5rem;
        margin-left: 2rem;
    `}
  }

  ${({ $bottomRightAlignment }) =>
    $bottomRightAlignment &&
    `
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
      
      img {
        object-position: 50% 0%;
      }
  `}
`;

const FeaturedContent = styled.div<{ $mobilePadding?: string }>`
  padding: 1.5rem 2rem 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-bottom: 2.5rem;
  }
`;

const FontAwesomeArrow = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.colors.primaryRed};
  font-size: 1.25rem;
  position: absolute;
  top: auto;
  bottom: 1rem;
  left: auto;
  right: 1rem;
`;

interface CtaCardProps {
  link: string;
  target?: string;
  bgColor?: string;
  hoverBgColor?: string;
  mobileBgColor?: string;
  mobilePadding?: string;
  fullHeight?: boolean;
  isCornerImage?: boolean;
  bottomRightAlignment?: boolean;
  fadeIn?: boolean;
  children: ReactNode;
  imgComponent: ReactNode;
}

const CtaCard = forwardRef<HTMLAnchorElement, CtaCardProps>((props, ref) => {
  const {
    link,
    target,
    children,
    bgColor,
    hoverBgColor,
    mobileBgColor,
    fullHeight,
    isCornerImage,
    mobilePadding,
    bottomRightAlignment,
    fadeIn,
    imgComponent,
  } = props;
  return (
    <CtaCardContainer
      href={withPrefix(link)}
      ref={ref}
      target={target}
      $bgColor={bgColor}
      $hoverBgColor={hoverBgColor}
      $mobileBgColor={mobileBgColor}
      $fullHeight={fullHeight}
      $fadeIn={fadeIn}
    >
      <FeaturedImageContainer $isCornerImage={isCornerImage} $bottomRightAlignment={bottomRightAlignment}>
        {imgComponent}
      </FeaturedImageContainer>
      <FeaturedContent $mobilePadding={mobilePadding}>{children}</FeaturedContent>
      <FontAwesomeArrow icon={faArrowRight} />
    </CtaCardContainer>
  );
});

export default CtaCard;
